<template>
  <div class="relative w-full flex flex-col items-center text-white bg-[#120F14] mt-[70px] pb-[80px]">
    <!-- Spinner while loading -->
    <div v-if="loading" class="flex justify-center items-center min-h-screen w-full">
      <div class="twist-spinner w-[60px] h-[60px]"></div>
    </div>

    <div v-else class="w-full h-full flex flex-col items-center justify-start">
      <!-- Header Container -->
      <div class="w-full max-w-[1400px] mx-auto pt-6 px-4 lg:px-8 xl:px-10">
        <!-- Sub Header Row -->
        <div class="flex flex-col md:flex-row items-start md:items-center justify-between w-full mb-6 gap-4">
          <div class="flex flex-wrap items-center gap-4">
            <button @click="$router.push('/raffle')" class="flex items-center justify-center w-8 h-8 rounded bg-[#1A1D24] hover:bg-[#2A2E38] transition-colors border border-white/5">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
            </button>
            
            <div class="px-2 py-1 rounded-[4px] text-[10px] font-bold uppercase tracking-wider" 
                 :class="raffleStatus === 'ended' ? 'bg-[#5A7E22] text-[#B5E550]' : 'bg-[#00F0FF]/20 text-[#00F0FF]'">
              {{ raffleStatus === 'ended' ? $t('Raffle.EndedRaffle') : $t('Raffle.ActiveRaffle') }}
            </div>
            
            <h2 class="text-xl md:text-2xl font-bold text-white uppercase font-sans m-0 truncate max-w-[500px]">
              {{ raffleName }}
            </h2>
          </div>
        </div>

        <!-- Details Hero Layout -->
        <div class="w-full flex flex-col xl:flex-row items-center xl:items-start justify-center gap-10 mb-10">
          
          <!-- LEFT: Image & Actions -->
          <div class="flex flex-col items-center w-full max-w-[400px]">
            <div class="relative w-full aspect-square bg-[#1A1D24] rounded-2xl border border-white/5 flex items-center justify-center p-8 overflow-hidden shadow-[0_0_30px_rgba(0,240,255,0.05)]">
              <!-- subtle background glow -->
              <div class="absolute inset-0 bg-gradient-to-b from-[#00F0FF]/10 to-transparent"></div>
              <img
                :src="raffleImage || require('@/assets/store-fallback.png')"
                alt="Prize"
                class="w-full h-full object-contain relative z-10"
              />
            </div>

            <!-- Buttons -->
            <button
              @click="openVerificationModal"
              class="w-full mt-6 px-6 py-4 rounded-xl border border-[#00F0FF]/20 bg-[#14181F] text-[#00F0FF] font-black text-sm tracking-widest uppercase hover:bg-[#00F0FF]/10 transition-colors shadow-[0_0_15px_#00F0FF15]"
            >
              {{ $t('Raffle.Verify') }}
            </button>

            <button
              @click="raffleStatus !== 'ended' ? (showRaffleModal = true) : null"
              :disabled="raffleStatus === 'ended'"
              class="w-full mt-4 px-6 py-4 rounded-xl font-black text-sm tracking-widest uppercase transition-all flex items-center justify-center gap-2"
              :class="raffleStatus === 'ended' ? 'bg-[#2A2E38] text-gray-500 cursor-not-allowed' : 'bg-[#00F0FF] text-black hover:bg-[#1ED6FF] shadow-[0_0_20px_#00F0FF40]'"
            >
              <template v-if="raffleStatus === 'ended'">{{ $t('Raffle.RaffleEnded') }}</template>
              <template v-else>
                {{ $t('Raffle.JoinFor') }}
                <img src="@/assets/coin.png" alt="Coin" class="w-5 h-5 object-contain" />
                {{ raffleEntryCost }}
              </template>
            </button>
          </div>

          <!-- RIGHT: Info Cards -->
          <div class="flex-1 w-full bg-[#14181F] rounded-2xl border border-white/5 p-6 md:p-8 shadow-[0_0_20px_rgba(0,0,0,0.5)]">
            <div class="flex justify-between items-start mb-6 gap-4">
              <h2 class="text-2xl sm:text-3xl font-black text-white uppercase tracking-wide">
                {{ raffleName }}
              </h2>
              <button
                @click="showHowItWorksModal = true"
                class="flex items-center gap-2 text-xs sm:text-sm text-[#00F0FF] font-bold tracking-wider hover:opacity-80 transition-opacity whitespace-nowrap"
              >
                <div class="w-4 h-4 flex items-center justify-center rounded-full border-2 border-[#00F0FF] text-[10px]">i</div>
                {{ $t('Raffle.HowItWorks') }}
              </button>
            </div>

            <p class="text-gray-400 text-sm sm:text-base leading-relaxed mb-8">
              {{ raffleDescription }}
            </p>

            <!-- Stats Grid -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <!-- Entries -->
              <div class="flex items-center justify-between px-5 py-4 rounded-xl bg-[#1A1D24] border border-white/5">
                <div class="flex items-center gap-3 text-gray-500 text-[11px] font-bold uppercase tracking-widest">
                  <img src="@/assets/users.png" class="w-4 h-4 opacity-50" />
                  {{ $t('Raffle.Entries') }}
                </div>
                <span class="text-white font-bold">{{ raffleTotalEntries.toLocaleString() }}</span>
              </div>
              
              <!-- Prize Places -->
              <div class="flex items-center justify-between px-5 py-4 rounded-xl bg-[#1A1D24] border border-white/5">
                <div class="flex items-center gap-3 text-gray-500 text-[11px] font-bold uppercase tracking-widest">
                  <img src="@/assets/crown-raffles.png" class="w-4 h-4 opacity-50" />
                  {{ $t('Raffle.PrizePlaces') }}
                </div>
                <span class="text-white font-bold">{{ raffleNumberOfWinners }}</span>
              </div>

              <!-- Ended On -->
              <div class="flex items-center justify-between px-5 py-4 rounded-xl bg-[#1A1D24] border border-white/5">
                <div class="flex items-center gap-3 text-gray-500 text-[11px] font-bold uppercase tracking-widest">
                  <img src="@/assets/calendar-raffles.png" class="w-4 h-4 opacity-50" />
                  {{ $t('Raffle.EndedOn') }}
                </div>
                <span class="text-white font-bold">{{ formatDate(raffleDeadline) }}</span>
              </div>

              <!-- Your Tickets -->
              <div class="flex items-center justify-between px-5 py-4 rounded-xl bg-[#1A1D24] border border-[#00F0FF]/20 shadow-[0_0_10px_#00F0FF10]">
                <div class="flex items-center gap-3 text-[#00F0FF] text-[11px] font-bold uppercase tracking-widest">
                  <img src="@/assets/ticket-raffles.png" class="w-4 h-4 opacity-80" />
                  {{ $t('Raffle.YourTickets') }}
                </div>
                <span class="text-[#00F0FF] font-black text-lg">{{ totalUserTickets }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Winner Section -->
        <div v-if="raffleWinners && raffleWinners.length > 0" class="w-full mb-10">
          <h2 class="text-xl font-bold text-white uppercase tracking-widest flex items-center mb-6">
            <svg class="w-5 h-5 mr-3 text-[#00F0FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            {{ raffleWinners.length === 1 ? $t('Raffle.Winner') : $t('Raffle.Winners') }}
          </h2>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div v-for="(winner, index) in raffleWinners" :key="index" class="relative bg-[#1A1D24] rounded-2xl p-[1px] overflow-hidden group hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-[0_10px_30px_rgba(0,240,255,0.15)] animate-glow-pulse z-10">
              <div class="absolute inset-0 bg-gradient-to-b from-[#00F0FF] to-transparent opacity-30 group-hover:opacity-60 transition-opacity duration-500"></div>
              <div class="relative bg-[#14181F] rounded-[15px] p-6 flex flex-col items-center h-full">
                <img :src="winner.profileImageUrl" class="w-16 h-16 rounded-full border-2 border-[#00F0FF] mb-4 shadow-[0_0_15px_#00F0FF50]" />
                <h3 class="text-lg font-bold text-white mb-1 truncate w-full text-center">{{ winner.user.username }}</h3>
                
                <div class="mt-4 w-full bg-white/5 rounded-lg p-3 border border-white/5 flex flex-col items-center gap-1">
                  <span class="text-[10px] text-gray-500 font-bold uppercase tracking-widest">{{ $t('Raffle.WinningTicket') }}</span>
                  <div class="flex items-center gap-2">
                    <img src="@/assets/ticket-raffles.png" class="w-3 h-3 opacity-80" />
                    <span class="text-[#00F0FF] font-bold text-xs truncate max-w-full">{{ winner.ticketId }}</span>
                  </div>
                </div>
                
                <div class="mt-3 text-[10px] text-gray-400 font-bold uppercase tracking-widest flex items-center gap-2">
                  {{ $t('Raffle.TotalEntries') }}: <span class="text-white">{{ winner.totalEntries }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Entries Section (Table) -->
        <div class="w-full bg-[#14181F] rounded-xl border border-[#00F0FF]/20 p-6 shadow-[0_0_12px_#00F0FF20] mb-6">
          <div class="flex justify-between items-center mb-6">
            <h2 class="text-xl font-bold text-white uppercase flex items-center tracking-widest">
              <img src="@/assets/users.png" class="w-5 h-5 mr-3 object-contain" /> {{ $t('Raffle.EntriesCaps') }}
            </h2>
          </div>

          <div class="w-full overflow-x-auto custom-scroll">
            <div class="min-w-[600px] flex flex-col">
              <!-- Table Header -->
              <div class="grid grid-cols-2 gap-4 pb-4 border-b border-white/5 text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                <div>{{ $t('Raffle.User') }}</div>
                <div class="text-right">{{ $t('Raffle.TotalEntries') }}</div>
              </div>

              <!-- Table Body -->
              <div v-for="(entry, index) in entries" :key="index" class="grid grid-cols-2 gap-4 py-4 items-center border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors">
                <div class="flex items-center gap-3">
                  <span class="text-sm font-bold text-white truncate max-w-[200px]">{{ entry.name }}</span>
                </div>
                <div class="text-right text-sm font-black text-[#00F0FF]">
                  {{ entry.total }}
                </div>
              </div>

              <div v-if="entries.length === 0" class="py-8 text-center text-gray-500 text-xs font-bold uppercase tracking-widest">
                {{ $t('Raffle.NoEntriesFound') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="w-full mt-6 mb-12">
          <!-- Desktop Pagination -->
          <div class="hidden lg:block w-full">
            <div class="p-[1px] bg-gradient-to-r from-[#00F0FF] to-[#A144F6] rounded-xl">
              <div class="bg-[#14181F] rounded-[11px] px-6 py-4 flex justify-between items-center w-full">
                <p class="text-gray-400 font-bold text-sm tracking-wider">
                  {{ $t('Store.Showing') }} <span class="text-white">{{ itemStart }}</span> - <span class="text-white">{{ itemEnd }}</span> {{ $t('Store.Of') }} <span class="text-white">{{ totalItems }}</span> {{ $t('Store.Items') }}
                </p>
                <div class="flex items-center gap-2">
                  <button @click="prevPage" :disabled="currentPage === 1" class="flex items-center justify-center w-10 h-10 rounded-md bg-[#0D0B10] text-white disabled:opacity-50 transition-colors hover:bg-white/5 border border-transparent">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <span v-for="page in totalPages" :key="page">
                    <button @click="goToPage(page)" :class="['w-10 h-10 rounded-md font-bold transition-all border', currentPage === page ? 'bg-[#0D0B10] border-[#00F0FF] text-[#00F0FF]' : 'bg-[#0D0B10] border-transparent text-gray-500 hover:text-white']">
                      {{ page }}
                    </button>
                  </span>
                  <button @click="nextPage" :disabled="currentPage === totalPages" class="flex items-center justify-center w-10 h-10 rounded-md bg-[#0D0B10] text-white disabled:opacity-50 transition-colors hover:bg-white/5 border border-transparent">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile Pagination -->
          <div class="flex flex-col items-center lg:hidden w-full">
            <div class="w-full p-[1px] bg-gradient-to-r from-[#00F0FF] to-[#A144F6] rounded-xl">
              <div class="bg-[#14181F] rounded-[11px] p-4 flex flex-col items-center w-full">
                <p class="text-gray-400 font-bold text-sm tracking-wider mb-4">
                  {{ $t('Store.Showing') }} <span class="text-white">{{ itemStart }}</span> - <span class="text-white">{{ itemEnd }}</span> {{ $t('Store.Of') }} <span class="text-white">{{ totalItems }}</span> {{ $t('Store.Items') }}
                </p>
                <div class="flex flex-wrap justify-center gap-2">
                  <button @click="prevPage" :disabled="currentPage === 1" class="flex items-center justify-center w-12 h-12 rounded-md bg-[#0D0B10] text-white disabled:opacity-50 transition-colors hover:bg-white/5 border border-transparent">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <span v-for="page in totalPages" :key="page">
                    <button @click="goToPage(page)" :class="['w-12 h-12 rounded-md font-bold transition-all border', currentPage === page ? 'bg-[#0D0B10] border-[#00F0FF] text-[#00F0FF]' : 'bg-[#0D0B10] border-transparent text-gray-500 hover:text-white']">
                      {{ page }}
                    </button>
                  </span>
                  <button @click="nextPage" :disabled="currentPage === totalPages" class="flex items-center justify-center w-12 h-12 rounded-md bg-[#0D0B10] text-white disabled:opacity-50 transition-colors hover:bg-white/5 border border-transparent">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <RaffleEntryModal
    :showRaffleModal="showRaffleModal"
    :raffleName="raffleName"
    :entryCost="raffleEntryCost"
    :loading="loading"
    @confirmEntry="enterRaffle"
    @closeRaffleModal="showRaffleModal = false"
  />

  <RaffleVerificationModal
    v-if="showVerificationModal"
    :showVerificationModal="showVerificationModal"
    :raffleWinners="raffleWinners"
    @close="showVerificationModal = false"
  />

  <RaffleSuccessModal
    :showSuccessModal="showSuccessModal"
    :raffleName="raffleName"
    :ticket="ticket"
    :totalEntries="totalEntries"
    :ticketIds="ticketIds"
    @closeSuccessModal="closeSuccessModal"
  />

  <ErrorModal
    v-show="showErrorModal"
    :show="showErrorModal"
    :title="errorTitle"
    :message="errorMessage"
    @close="closeErrorModal"
  />

  <HowRafflesWorkModal :show="showHowItWorksModal" @close="closeHowItWorksModal" />
</template>

<script>
import axios from "axios";
// import RedirectModal from "@/components/RedirectModal.vue";
import ErrorModal from "@/components/ErrorModal.vue";
import HowRafflesWorkModal from "@/components/HowRafflesWorkModal.vue";
import RaffleEntryModal from "@/components/RaffleEntryModal.vue";
import RaffleSuccessModal from "@/components/RaffleSuccessModal.vue";
import RaffleVerificationModal from "@/components/RaffleVerificationModal.vue";

export default {
  name: "RaffleDetailsPage",
  components: {
    ErrorModal,
    HowRafflesWorkModal,
    RaffleEntryModal,
    RaffleSuccessModal,
    RaffleVerificationModal,
  },
  data() {
    return {
      // User
      spinsala: null,
      points: 0,
      username: "",
      userId: "",

      // UI Modals
      showRedirectModal: false,
      showRaffleModal: false,
      showErrorModal: false,
      showSuccessModal: false,
      showHowItWorksModal: false,
      showVerificationModal: false,

      // UI State
      loading: false,
      errorMessage: "",

      // Raffle Details
      selectedRaffle: null,
      raffleName: "",
      raffleDescription: "",
      raffleImage: "",
      raffleStatus: "",
      raffleDeadline: null,
      raffleEntryCost: 0,
      raffleNumberOfWinners: 0,
      raffleTotalEntries: 0,
      raffleEntries: [],
      raffleWinners: [],
      ticketIds: [],

      // Pagination
      currentPage: 1,
      itemsPerPage: 20,
    };
  },
  computed: {
    totalUserTickets() {
      return this.raffleEntries
        .filter((entry) => entry.user.username === this.username)
        .reduce((total, entry) => total + entry.totalEntries, 0);
    },
    paginatedEntries() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.raffleEntries.slice(start, start + this.itemsPerPage);
    },
    itemStart() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    itemEnd() {
      const end = this.currentPage * this.itemsPerPage;
      return end > this.totalItems ? this.totalItems : end;
    },
    totalItems() {
      return this.raffleEntries.length;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    entries() {
      return this.paginatedEntries.map((entry) => ({
        name: entry.user?.username || "",
        total: entry.totalEntries || 0,
      }));
    },
  },
  methods: {
    async fetchUser() {
      const token = localStorage.getItem("maherco-access-token");
      if (!token) return console.error("No access token found.");

      try {
        const { data } = await axios.get(`${process.env.VUE_APP_BASE_API}/api/users/me`, {
          headers: {
            "x-api-key": process.env.VUE_APP_X_API_KEY,
            Authorization: `Bearer ${token}`,
          },
        });

        const user = data.user;
        this.points = user.attributes?.points || 0;
        this.username = user.username;
        this.userId = user._id;
      } catch (error) {
        console.error("Error fetching user data:", error);
        if (error.response?.status === 401) {
          localStorage.removeItem("vuex");
          localStorage.removeItem("maherco-access-token");
          window.location.reload();
        }
      }
    },

    async fetchRaffleData() {
      const raffleId = this.$route.params.id;
      if (!raffleId) return console.error("No raffleId found in the URL.");

      this.loading = true;

      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_BASE_API}/api/raffles/${raffleId}`,
          {
            headers: { "x-api-key": process.env.VUE_APP_X_API_KEY },
          }
        );

        const raffle = data.raffle;
        this.selectedRaffle = raffle;
        this.raffleName = raffle.name;
        this.raffleDescription = raffle.description;
        this.raffleImage = raffle.imageUrl;
        this.raffleEntries = data.entries;
        this.raffleStatus = raffle.status;
        this.raffleEntryCost = raffle.entryCost;
        this.raffleDeadline = raffle.deadline;
        this.raffleNumberOfWinners = raffle.numberOfWinners;
        this.raffleTotalEntries = raffle.entries || 0;
        this.raffleWinners = raffle.winners.map((winner) => ({
          ...winner,
          profileImageUrl: winner.user.profileImage || require("@/assets/avatar-1.png"),
        }));
      } catch (error) {
        console.error("Error fetching raffle data:", error);
      } finally {
        this.loading = false;
      }
    },

    async enterRaffle(entries) {
      const token = localStorage.getItem("maherco-access-token");
      const baseUrl = process.env.VUE_APP_BASE_API;

      if (!token) return this.showError("You must be logged in to enter a raffle.");
      if (!this.selectedRaffle) return this.showError("No raffle selected.");

      this.loading = true;

      try {
        const { data, status } = await axios.post(
          `${baseUrl}/api/raffles/${this.selectedRaffle._id}/join`,
          { entriesCount: entries },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-api-key": process.env.VUE_APP_X_API_KEY,
            },
          }
        );

        if (status === 200 && data.message === "Entry successfully added to raffle") {
          this.ticket = entries;
          this.totalEntries = data.totalEntries;
          this.ticketIds = data.allTicketIds || [];

          this.showRaffleModal = false;
          this.showErrorModal = false;
          this.showSuccessModal = true;
        } else {
          this.showError("Unexpected response format.");
        }
      } catch (error) {
        console.error("❌ Error entering raffle:", error);
        this.handleRaffleError(error);
      } finally {
        this.loading = false;
      }
    },

    handleRaffleError(error) {
      const message =
        error.response?.data?.message || "An error occurred while entering the raffle.";
      console.error("API Error:", message);
      this.showError(message);
    },

    showError(message) {
      this.errorMessage = message;
      this.showErrorModal = true;
      this.showRaffleModal = false;
    },

    closeErrorModal() {
      this.showErrorModal = false;
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
      this.fetchRaffleData();
    },

    openRaffleModal(raffle) {
      this.selectedRaffle = raffle;
      this.showRaffleModal = true;
    },

    openHowItWorksModal() {
      this.showHowItWorksModal = true;
    },

    openVerificationModal() {
      this.showVerificationModal = true;
    },

    closeHowItWorksModal() {
      this.showHowItWorksModal = false;
    },

    getRaffleTooltip(status) {
      if (status === "stopped") return "Raffle entries are not available at the moment.";
      if (status === "ended") return "This raffle has already ended.";
      return "";
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString();
    },

    goToRaffles() {
      this.$router.push("/raffle");
    },

    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },

    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },

    goToPage(page) {
      this.currentPage = page;
    },
  },
  mounted() {
    this.fetchUser();
    this.fetchRaffleData();
  },
};
</script>


<style scoped>
@keyframes scroll-dot {
  0% {
    transform: translate(20%, -50%); /* Start exactly at left */
    opacity: 0.8;
  }
  50% {
    transform: translate(270%, -50%); /* Move to far right */
    opacity: 1;
  }
  100% {
    transform: translate(20%, -50%);
    opacity: 0.8;
  }
}

.animate-scroll-dot {
  animation: scroll-dot 5s infinite ease-in-out;
}
</style>
